import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import {
  BrowserDataType,
  BrowserType,
  BrowserIndexMap,
  IKameleoonData,
  KameleoonData,
  BrowserTypeMap,
} from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * Browser - a class for creating an instance for browser of a certain type
 * */
export class Browser implements IKameleoonData {
  public status: TrackingStatus;

  private browser: BrowserType;
  private version?: number;

  /**
   * @param {BrowserType} browser - one of the browser types available for data, if browser can not be found in a list, it can be marked as `BrowserType.Other`
   * @param {number | undefined} version - version of the browser, floating point number represents major and minor version of the browser
   * @example
   * ```ts
   * const browser = new Browser(BrowserType.Chrome, 86);
   * const browser = new Browser(BrowserType.Safari, 14.1);
   * ```
   * */
  constructor(browser: BrowserType, version?: number) {
    this.browser = browser;
    this.version = version;
    this.status = TrackingStatus.Unsent;
  }

  get url(): string {
    // --- Note ---
    // If SDK is used in vanilla JS codebase, then you're also able to create an instance
    // with no required data, we don't want send anything to tracking in that case
    if (!this.browser) {
      return '';
    }

    const browserVersion = this.version
      ? UrlParameter.BrowserVersion + this.version
      : '';

    return (
      UrlEventType.StaticData +
      UrlParameter.Browser +
      this.browser +
      UrlParameter.BrowserIndex +
      BrowserIndexMap[this.browser] +
      browserVersion
    );
  }

  get data(): BrowserDataType {
    return {
      browser: this.browser,
      type: KameleoonData.Browser,
      version: this.version,
      status: this.status,
    };
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a Browser instance from raw data
   * @param {BrowserDataType} data - a raw data
   * @return {Browser} a Browser instance
   * */
  public static _fromRaw(data: BrowserDataType): Browser {
    const { browser: browserType, version, status } = data;
    const browser = new Browser(browserType, version);

    browser.status = status;

    return browser;
  }

  /**
   * @private
   * @method _fromVisit - an internal method for creating an instance of Browser class from a visit
   * @param {VisitType} visit - a visit
   * @returns {Browser | undefined} an instance of Browser class or undefined if browser can not be found among visits
   * */
  public static _fromVisit(visit: VisitType): Browser | undefined {
    if (!visit.staticDataEvent) {
      return undefined;
    }

    const {
      browser: browserType,
      browserIndex,
      browserVersion,
    } = visit.staticDataEvent.data;

    if (browserType || typeof browserIndex === 'number') {
      const browser = browserType ?? BrowserTypeMap[browserIndex as number];

      if (browserVersion) {
        return new Browser(browser, browserVersion);
      } else {
        return new Browser(browser);
      }
    }

    return undefined;
  }
}
